
import {throwError as observableThrowError,  Observable, of, forkJoin } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { ConfigService } from './config.service';

/*
**
ForecastService - Provides forecast information using 1 of several forecasting services.
	1.  To use, first the provider must be declared:
		In the configuration file, set "WeatherServices": { "forecastProvider": "DarkSky" }
			Other providers are:  "WU" for Weather Underground - still under development
			Other providers are:  "NWS" for National Weather Service- still under development
		For the observed, set In the configuration file, set "WeatherServices": { "observedProvider": "DarkSky" }
			Other providers are:  "NovaStar" for using precipitation and temperature data from NovaStar 

	2.  Because the forecast must be read before it can be displayed, this service must be used in the following way:
			this.forecastService.readForecast()						// First the forecast must be read
					.subscribe ((forecast) => {						// and subscribe must be called in order for that read to commence
						this.forecastService.setForecast(forecast);	// Next, set the forecast with the returned data.
						// now additional forecast service routines are available to be called
						var next3Hours = this.forecastService.getNext3HoursForecast();
						this.weatherInfo = {
							"next3Hours": next3Hours.icon,
							"next3HoursMsg": next3Hours.summary
						};
				});
**
*/
@Injectable()
export class ForecastService {
	private http: HttpClient;
	private forecastProvider: String = 'DarkSky';  // set a default
	private forecastUrl;
	private observedProvider: String = 'DarkSky';  // set a default

	// The following are json objects to store forecast and observed weather information.
	// These objects shouldn't be accessed directly since every forecastProvider format may be different.
	// Helper functions should be called, i.e. getNext3HoursForecast()
	private forecast;
	private observed15Minutes;
	private observedHour;
	private observed3Hours;
	private observedDay;
	private observedWeek;

	private configService: ConfigService;
	systemConfiguration: any;
	baseDataDir: string;
	baseDir; string;
	baseWeatherDir: string;
	baseImagesDir = 'assets/images';
	NovaStarPrecipLocId = '';

	constructor ( http: HttpClient, configService: ConfigService) {
		this.http = http;
		this.configService = configService;
		this.setSystemConfiguration();
	}

	//
	// setSystemConfiguration MUST be called before any of the other parse* routines in this class can be called.
	//
	setSystemConfiguration ( ) {
		this.systemConfiguration = this.configService.systemConfiguration;
		this.baseDataDir = this.configService.baseDataDir;
		this.baseDir = this.configService.baseDir;
		this.baseWeatherDir = this.configService.baseDir + 'weather/';
		if ( this.systemConfiguration?.WeatherServices?.forecastProvider) {
			this.forecastProvider = this.systemConfiguration.WeatherServices.forecastProvider;
			this.forecastUrl = this.systemConfiguration.WeatherServices?.forecastUrl;
		}
		if ( this.systemConfiguration?.WeatherServices?.observedProvider) {
			this.observedProvider = this.systemConfiguration.WeatherServices.observedProvider;
		}
		// console.log(this.forecastProvider);
		// console.log(this.observedProvider);
		// console.log(this.baseWeatherDir);
	}

	setForecastProvider ( forecastProvider: String ) {
		this.forecastProvider = forecastProvider;
	}

	setObservedProvider ( observedProvider: String ) {
		this.observedProvider = observedProvider;
	}

	//
	// read a forecast file - currently this routine reads the forecast and stores the
	// 		results but returning an observable may be necessary in the future so we know when the routine has completed.
	//
	readForecast (): Observable<any> {
		console.log(this.forecastProvider);
		if ( this.forecastProvider === 'DarkSky' ) {
			const filename = this.baseWeatherDir + 'darkSkyForecast.json';
			console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for weather prediction information.' );
			// return this.http.get(filename, {responseType: 'text'})
			return this.http.get(filename)
				.pipe( catchError(this.handleError));
		} else if ( this.forecastProvider == 'NWS') {
			console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + this.forecastUrl + ' for weather prediction information.' );
			return this.http.get(this.forecastUrl).pipe(switchMap((config: Object, index) => 
				config 
					? forkJoin ({
						forecast: this.http.get(config['properties'].forecast),
						forecastGridData: this.http.get(config['properties'].forecastGridData)})
					: of()
  			));
		}
	}

	// read an observed weather file
	readObservedWeatherData (): Observable<any> {
		let observable:Observable<any>;

		if ( this.observedProvider === 'DarkSky' ) {
			const filename = this.baseWeatherDir + 'darkSkyObserved15min.json';
			const filename2 = this.baseWeatherDir + 'darkSkyObserved1day.json';
			const filename3 = this.baseWeatherDir + 'darkSkyObserved7day.json';
			observable = forkJoin([
				this.http.get(filename, {responseType: 'text'}),
				this.http.get(filename2, {responseType: 'text'}),
				this.http.get(filename3, {responseType: 'text'})
			])
		} else {
			// use Novastar data
			// forkJoin the following - set up using configuration like other dws url
			this.NovaStarPrecipLocId = this.systemConfiguration?.WeatherServices?.NovaStarPrecipLocId;
			const NovaStarTempAirLocId = this.systemConfiguration?.WeatherServices?.NovaStarTempAirLocId;
			if ( this.NovaStarPrecipLocId == null ) {
				console.log('Warning: The Operator configuration file does not define a representative weather station (NovaStarWeatherLocId).');
				return of({});
			}

			// set air temperature data interval to
			// value in configuration or IrregSecond 
			const NovaStarTempAirDataInterval = this.systemConfiguration?.WeatherServices?.NovaStarTempAirDataInterval != null ?
			this.systemConfiguration.WeatherServices.NovaStarTempAirDataInterval :
			'IrregSecond';

			// set precip data interval to
			// value in configuration or IrregSecond 
			const NovaStarPrecipDataInterval = this.systemConfiguration?.WeatherServices?.NovaStarPrecipDataInterval != null ?
			this.systemConfiguration.WeatherServices.NovaStarPrecipDataInterval :
			'IrregSecond';

			// console.log ( NovaStarTempAirDataInterval);
			// console.log ( NovaStarPrecipDataInterval);

			const c1 = this.configService.createURI({
					'filename': 'ts', 
					'locId': NovaStarTempAirLocId, 
					'dataType': 'TempAir', 
					'dataInterval': NovaStarTempAirDataInterval,
					'format': 'json'}) + '&periodStart=now_minus_7Day';
			const c2 = this.configService.createURI({
					'filename': 'ts',
					'locId': this.NovaStarPrecipLocId,
					'dataType': 'Precip', 
					'dataInterval': NovaStarPrecipDataInterval,
					'format': 'json'}) + '&periodStart=now_minus_7Day';
			const c3 = this.configService.createURI ({'filename': 'alarmTriggers'});
			const c4 = this.configService.createURI({'filename': 'alarms'}) + '?periodStart=now_minus_7Day';
			console.log(c1, c2, c3, c4);

			observable = forkJoin([ 
				this.http.get(c1), 
				this.http.get(c2), 
				this.http.get(c3), 
				this.http.get(c4) ]);
		}

		return observable;
	}


	// The forecasted and observed files have been read and now the content should be saved for further access.
	setForecast( forecast ) {
		this.forecast = forecast;
		// console.log(forecast);

		const userPreferences = this.configService.getUserPreference('userPreferences');
		// console.log( userPreferences ); // why is this undefined ... revisit!
	}

	// This routine sets the icon and summary values for:
	// this.observed15Minutes
	// this.observedHour
	// this.observed3Hours
	// this.observedDay
	// this.observedWeek
	setObservedWeatherData ( observed ) {
		if ( this.observedProvider === 'DarkSky' ) {
			// the observed array should contain
			// 0 - darkSkyObserved15min.json
			// 1 - darkSkyObserved1day.json
			// 2 - darkSkyObserved7day.json
			const t1 = JSON.parse(observed[0]);
			this.observed15Minutes = {
				'icon': t1.minutely.icon,
				'summary': t1.minutely.summary
			}

			this.observedHour =  {
				'icon': t1.hourly.icon,
				'summary': t1.hourly.summary
			}

			this.observed3Hours =  {
				'icon': t1.hourly.icon,
				'summary': t1.hourly.summary
			}

			const t4 = JSON.parse(observed[1]);
			this.observedDay =  {
				'icon': t4.hourly.icon,
				'summary': t4.hourly.summary
			}

			const t5 = JSON.parse(observed[2]);
			this.observedWeek = { 
					'icon': t5.daily.data[0].icon,
					'summary': t5.daily.data[0].summary 
			}
		} else {
			// using NovaStar data
			// the observed array should contain the following data for the representative station
			// 0 - temperature
			// 1 - precipitation
			// 2 - alarm triggers
			// 3 - alarms
			// console.log(observed);
			const precipTriggerObjects = this.NovaStarGetAlarmPrecipTriggers(observed[2]);
			const alarms = this.NovaStarGetPrecipAlarmsForWeatherStation (precipTriggerObjects, observed[3]);
			this.observed15Minutes = this.NovaStarCreateSummaryFromData(
					precipTriggerObjects, alarms, observed[1], observed[0], '15Min');
			this.observedHour = this.NovaStarCreateSummaryFromData(
					precipTriggerObjects, alarms, observed[1], observed[0], '1Hour');
			this.observed3Hours = this.NovaStarCreateSummaryFromData(
					precipTriggerObjects, alarms, observed[1], observed[0], '3Hour');
			this.observedDay = this.NovaStarCreateSummaryFromData(
					precipTriggerObjects, alarms, observed[1], observed[0], 'LastDay');
			this.observedWeek = this.NovaStarCreateSummaryFromData(
					precipTriggerObjects, alarms, observed[1], observed[0], 'LastWeek');
		}
	}

	private handleError (error: Response | any) {
		let errMsg: string;
		if (error instanceof Response) {
			const body = error.json() || '';
			const err = body['error'] || JSON.stringify(body);
			errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
		} else {
			errMsg = error.message ? error.message : error.toString();
		}
		console.error(errMsg);
		return observableThrowError(errMsg);
	}

	private extractData(res: Response) {
		const body = JSON.stringify(res.json());
		return body || '';
	}

	getProviderURL () {
		if ( this.forecastProvider === 'DarkSky' ) {
			return 'https://darksky.net/forecast/' + this.forecast.latitude + ',' + this.forecast.longitude + '/us12/en';
		} else if ( this.forecastProvider === 'NWS' ) {
			return 'https://www.weather.gov/documentation/services-web-api/';
		}
	}

	// formatDateToHour time a UTC value and converts the hour to a string: 12 midnight, 12 noon, **AM, or **PM
	formatDateToHour(timeI: number): string {  // may need to do something with timezone !!!!!!
		const date = new Date(timeI * 1000);
		const time = date.getHours();
		let timeString;
		if ( time === 0 || time === 24 ) {
			timeString = '12 midnight';
		} else if ( time < 12 ) {
			timeString = time + 'AM';
		} else if ( time === 12 ) {
			timeString = '12 noon';
		} else {
			timeString = (time - 12) + 'PM';
		}
		return date.toLocaleDateString() + ', ' + timeString;
	}

	getNext6HoursForecast() {
		if ( this.forecastProvider === 'DarkSky' ) {
			console.log(this.forecast.hourly);
			const sixHourForecast = {};
			sixHourForecast['icon'] = this.translateIcon(this.forecastProvider, this.forecast.hourly.icon);
			sixHourForecast['precipProbability'] = ( Math.max(this.forecast.hourly.data[0].precipProbability,
				this.forecast.hourly.data[1].precipProbability, this.forecast.hourly.data[2].precipProbability,
				this.forecast.hourly.data[3].precipProbability, this.forecast.hourly.data[4].precipProbability, 
				this.forecast.hourly.data[5].precipProbability)
				* 100 ).toFixed(0);

			let summary = this.forecast.hourly.summary + '\n';
			for ( let i = 0; i <= 5; i++ ) {
				summary += this.summarizeDarkSkyPrecipData ( this.forecast, i);
			}
			sixHourForecast['summary'] = summary;

			return sixHourForecast;
		}
		else if ( this.forecastProvider === 'NWS' ) {
			const sixHourForecast = {};
			const today = this.getToday(false);
			// console.log('Today:', today);
			const periods = this.getNWSForecastForDateRange ( this.forecast.forecast, today );
			if ( periods != null && periods.length > 0 ) { 
				sixHourForecast['icon'] = this.translateIcon(this.forecastProvider, periods[0]?.icon);
				sixHourForecast['precipProbability'] = 
					this.getNWSForecastGridForDateRange ( this.forecast.forecastGridData, 
					today, 'probabilityOfPrecipitation', 'max' );
				sixHourForecast['summary'] = this.formulateNWSSummary(periods);
			}

			return sixHourForecast;
		} else {
			return null;
		}
	}

	getNextDayForecast() {
		// console.log(this.forecast);
		if ( this.forecastProvider === 'DarkSky' ) {
			const dayForecast = {};
			dayForecast['icon'] = this.translateIcon(this.forecastProvider, this.forecast.hourly.icon);
			dayForecast['precipProbability'] = ( Math.max(this.forecast.hourly.data[6].precipProbability,
				this.forecast.hourly.data[7].precipProbability, this.forecast.hourly.data[8].precipProbability,
				this.forecast.hourly.data[9].precipProbability, this.forecast.hourly.data[10].precipProbability,
				this.forecast.hourly.data[11].precipProbability) * 100 ).toFixed(0);

			let summary = this.forecast.hourly.summary + '\n';
			for ( let i = 6; i <= 12; i++ ) {
				summary += this.summarizeDarkSkyPrecipData ( this.forecast, i);
			}
			dayForecast['summary'] = summary;

			return dayForecast;
		}
		else if ( this.forecastProvider === 'NWS' ) {
			const dayForecast = {};
			const tomorrow = this.getFutureDay(1);
			// console.log('Tomorrow:', tomorrow);
			const periods = this.getNWSForecastForDateRange ( this.forecast.forecast, tomorrow);
			if ( periods != null && periods.length > 0 ) { 
				dayForecast['icon'] = this.translateIcon(this.forecastProvider, periods[0]?.icon);
				dayForecast['precipProbability'] = 
					this.getNWSForecastGridForDateRange ( this.forecast.forecastGridData, 
					tomorrow, 'probabilityOfPrecipitation', 'max' );
				dayForecast['summary'] = this.formulateNWSSummary(periods);
			}
			return dayForecast;
		} else {
			return null;
		}
	}

	getNextWeekForecast() {
		// console.log(this.forecast);
		if ( this.forecastProvider === 'DarkSky' ) {
			const weekForecast = {};

			weekForecast['icon'] = this.translateIcon(this.forecastProvider, this.forecast.daily.icon);
			weekForecast['precipProbability'] = ( Math.max(this.forecast.daily.data[0].precipProbability,
				this.forecast.daily.data[1].precipProbability, this.forecast.daily.data[2].precipProbability,
				this.forecast.daily.data[3].precipProbability, this.forecast.daily.data[4].precipProbability,
				this.forecast.daily.data[5].precipProbability, this.forecast.daily.data[6].precipProbability) * 100 ).toFixed(0);
			
			let summary = this.forecast.daily.summary + '\n';
			for ( let i = 0; i <= 6; i++) {
				summary += this.summarizeDarkSkyWeekForecast ( this.forecast, i );
			}
			weekForecast['summary'] = summary;

			return weekForecast;
		}
		else if ( this.forecastProvider === 'NWS' ) {
			const weekForecast = {};
			const nextWeek = this.getNextWeek(this.getFutureDay(2)[0]);
			// console.log('Next week:', nextWeek);
			const periods = this.getNWSForecastForDateRange ( this.forecast.forecast, nextWeek );
			if ( periods != null && periods.length > 0 ) { 
				weekForecast['icon'] = this.translateIcon(this.forecastProvider, periods[0]?.icon);
				weekForecast['precipProbability'] = 
					this.getNWSForecastGridForDateRange ( this.forecast.forecastGridData, 
					nextWeek, 'probabilityOfPrecipitation', 'max' );
				weekForecast['summary'] = this.formulateNWSSummary(periods);
			}
			return weekForecast;
		} else {
			return null;
		}
	}

	getObserved15Minutes() {
		// console.log(this.observed15Minute);
		const observed15minutes = {};
		observed15minutes['icon'] = this.translateIcon(this.observedProvider, this.observed15Minutes.icon);
		observed15minutes['summary'] = this.observed15Minutes.summary;
		return observed15minutes;
	}

	getObservedHour() {
		// console.log(this.observedHour);
		const observedHour = {};
		observedHour['icon'] = this.translateIcon(this.observedProvider, this.observedHour.icon);
		observedHour['summary'] = this.observedHour.summary;
		return observedHour;
	}

	getObserved3Hours() {
		// console.log(this.observed3Hour);
		const observed3Hours = {};
		observed3Hours['icon'] = this.translateIcon(this.observedProvider, this.observed3Hours.icon);
		observed3Hours['summary'] = this.observed3Hours.summary;
		return observed3Hours;
	}

	getObservedDay() {
		// console.log(this.observedDay);
		const observedDay = {};
		observedDay['icon'] = this.translateIcon(this.observedProvider, this.observedDay.icon);
		observedDay['summary'] = this.observedDay.summary;
		return observedDay;
	}

	getObservedWeek() {
		// console.log(this.observedWeek);
		const observedWeek = {};
		observedWeek['icon'] = this.translateIcon(this.observedProvider, this.observedWeek.icon);
		observedWeek['summary'] = this.observedWeek.summary;
		return observedWeek;
	}

	// Provider must be specified because this could either be the observed or forecasted provider.
	// ServiceIcon is the service-provider specified icon path.
	translateIcon(provider, serviceIcon) {
		if ( provider === 'DarkSky' ) {
			if ( serviceIcon.includes('partly-cloudy')) { return this.baseImagesDir + '/partlyCloudy.gif'; }
			if ( serviceIcon.includes('mostly-cloudy')) { return this.baseImagesDir + '/mostlyCloudy.gif'; }
			if ( serviceIcon.includes('clear')) { return this.baseImagesDir + '/clear.gif'; }
			if ( serviceIcon.includes('rain')) { return this.baseImagesDir + '/rain.gif'; }
			if ( serviceIcon.includes('snow')) { return this.baseImagesDir + '/snow.gif'; }
			if ( serviceIcon.includes('wind')) { return this.baseImagesDir + '/wind.gif'; }
			if ( serviceIcon.includes('sleet')) { return this.baseImagesDir + '/sleet.gif'; }
			if ( serviceIcon.includes('fog')) { return this.baseImagesDir + '/fog.gif'; }
			return this.baseImagesDir + '/unknown-weather.gif';
		} else if ( provider === 'NWS') {  
			// no translation necessary
			return serviceIcon;
		} else if ( provider === 'NovaStar') {  
			// see https://api.weather.gov/icons
			/*
			let path = serviceIcon.replace('https:\/\/api.weather.gov/icons', this.baseImagesDir );
			console.log(this.baseImagesDir, path);
			path = path.indexOf('?') > -1 ? path.slice(0, path.indexOf('?')) : path;
			console.log(path);
			path += '.png';
			return path;
			*/
			// serviceIcon should be something like 'day-rain'
			const tod = serviceIcon.includes('night') ? 'night' : 'day';
			if ( serviceIcon.includes('snow')) { return this.baseImagesDir + '/land/' + tod + '/snow.png'; }
			if ( serviceIcon.includes('rain')) { return this.baseImagesDir + '/land/' + tod + '/rain.png'; }
			if ( serviceIcon.includes('clear')) { return this.baseImagesDir + '/land/' + tod + '/few.png'; }
			return this.baseImagesDir + '/unknown-weather.gif';
		}
		return this.baseImagesDir + '/unknown-weather.gif';
	}

	// return array containing two dates in milliseconds: now and right before midnight
	getToday(includeNow) {
		let now = Date.now();
		let midnight = new Date(now);
		midnight.setHours(23, 59, 59);
		if ( includeNow == false ) {
			now = null;
		}
		return ([ now, midnight.getTime() ]);
	}

	// return array containing two dates in milliseconds: 
	// 		morning right after midnight and night right before midnight
	// 		of the number of days forward (numDays of 1 calculates tomorrow's values)
	getFutureDay(numDays) {
		const today = Date.now();
		const tomorrow = today + (numDays*24*60*60*1000); 	// add number of days to today
		let tomorrowMorning = new Date(tomorrow);
		tomorrowMorning.setHours(0, 0, 0);
		let tomorrowNight = new Date(tomorrow);
		tomorrowNight.setHours(23, 59, 59);
		return ([tomorrowMorning.getTime(), tomorrowNight.getTime()]);
	}

	// starting with startDate, return array containing two date in milliseconds: start date and date 7 days from now
	getNextWeek(startDate) {
		const now = Date.now();
		let today = new Date(startDate);
		const weekms = now + (7*24*60*60*1000); 	// add one week to today
		let week = new Date(weekms);
		week.setHours(23, 59, 59);
		return ([today.getTime(), week.getTime()]);
	}

	summarizeDarkSkyWeekForecast ( forecast, index ) {
		if ( forecast.daily.data.length > index ){
				let time = this.forecast.daily.data[index].time;
				const date = new Date(time * 1000);

				return ( date.toLocaleDateString() + ': ' + this.forecast.daily.data[index].summary +
					', ' + this.forecast.daily.data[index].precipIntensity + ' in ' +
					'(' + (this.forecast.daily.data[index].precipProbability * 100).toFixed(0) + '%)\n');
		} else { 
			return ('');
		}
	}

	// Given the DarkSky forecast, return a single string summarizing the conditions for the given index (hour).
	summarizeDarkSkyPrecipData ( forecast, index ){
		if ( forecast.hourly.data.length > index ) {
			return ( this.formatDateToHour(forecast.hourly.data[index].time) + ': ' + forecast.hourly.data[index].summary +
				', ' + forecast.hourly.data[index].precipIntensity + ' in ' +
				'(' + forecast.hourly.data[index].precipProbability * 100 + '%)\n');
		} else {
			return ('');
		}
	}

	// return the foreast period array that applies to the dateRange 
	// if dateRange[0] is null, the first date will be ignored
	// if dateRange[1] is null, the second date will be ignored
	getNWSForecastForDateRange ( forecast, dateRange ) {
		// console.log(forecast, dateRange );
		const periods = forecast?.properties?.periods;
		// console.log( periods );
		const returnPeriods = [];
		if ( periods == null || periods.length == 0 ) {
			return returnPeriods;
		}

		for ( let i = 0; i < periods.length; i++ ) {
			const currentPeriod = periods[i];
			const startTime = new Date(currentPeriod.startTime);
			const endTime = new Date(currentPeriod.endTime);
			// console.log(startTime, endTime, dateRange[0], dateRange[1] );
			if ( 
				( ( dateRange[0] == null ) || startTime > dateRange[0] ) && 
				( ( dateRange[1] == null ) || startTime < dateRange[1])
				// || ( endTime > dateRange[0]) && endTime < dateRange[1])  // maybe only let start time dictate?
			)
			{
				// console.log('Adding ', currentPeriod);
				returnPeriods.push(currentPeriod);
			}
		}
		return returnPeriods;
	}

	formulateNWSSummary(periods) {
		// console.log(periods);
		let summary = '';
		for ( let i = 0; i < periods.length; i++ ) {
			const currentPeriod = periods[i];
			// console.log('Adding ' + currentPeriod.name)
			summary += currentPeriod.name + ':' + currentPeriod.detailedForecast + '\n';
		}
		return summary;
	}

	// Using the forecastGridData from NWS, return the requested statistic for the given dataType within
	// the given dateRange.  StatisticType could be 'min', 'max', 'total', 'average'
	getNWSForecastGridForDateRange ( forecastGridData, dateRange, dataType, statisticType ) {
		const periods = forecastGridData.properties[dataType]?.values;
		// console.log( periods );
		if ( periods == null || periods.length == 0 ) {
			return 0;
		}

		const valueArray = [];
		for ( let i = 0; i < periods.length; i++ ) {
			const currentPeriod = periods[i];
			const time = currentPeriod.validTime.split('\/')[0];
			const validTimeObj = new Date(time);
			const validTime = validTimeObj.getTime();
			// console.log(currentPeriod, validTimeObj, validTime, dateRange)
			if ((dateRange[0] == null || validTime > dateRange[0]) && 
				(dateRange[1] == null || validTime < dateRange[1]))
			{
				// console.log('Adding ', currentPeriod);
				valueArray.push(currentPeriod.value);
			}
		}
		// console.log(dateRange, valueArray);
		if ( valueArray.length == 0 ) {
			return null;
		}

		let returnValue:any = 0;
		if ( statisticType == 'max') {
			returnValue = Math.max(...valueArray);
		} else if ( statisticType == 'min') {
			returnValue = Math.min(...valueArray);
		} else if ( statisticType == 'total') {
			returnValue = valueArray => valueArray.reduce((a,b) => a + b, 0);
		} else if ( statisticType == 'average') {
			returnValue = valueArray => valueArray.reduce((a,b) => a + b, 0) / valueArray.length;
		}

		return returnValue;

	}

	// return object with elements {icon: ..., summary: ...} for conditions going back as far as startTimeString
	// icon is a combination of time of day (day/night) and conditions (snow/rain/clear)
	// triggers is an array of ids for the defined triggers relating to "PrecipPresent"
	// alarms is the list of alarms for the weather station pertaining to "PrecipPresent"
	// precip, temperature are json objects directly from DWS
	// startTimeString is the start time string (15Min/1Hour/3Hour/LastDay/LastWeek)
	NovaStarCreateSummaryFromData(triggers, precipAlarmsArray, precip, temperature, startTimeString ) {
		// set up some defaults
		// create alarm name
		const alarmName = 'PrecipPresent-' + startTimeString;
		const unknownWeather = { 'icon': 'unknown', 'summary': 'Unknown.' };
		const triggerNotDefined = { 'icon': 'unknown', 
			'summary': 'An alarm trigger for ' + alarmName + ' has not been defined on this NovaStar system.' };
		let timeOfDay = 'day';

		// Determine time of day
		const now = Date.now();
		let durationMilliseconds = 0;
		switch (startTimeString) {
			case '15Min': durationMilliseconds = 15*60*1000; break;
			case '1Hour': durationMilliseconds = 60*60*1000; break;
			case '3Hour': durationMilliseconds = 3*60*60*1000; break;
			case 'LastDay': durationMilliseconds = 24*60*60*1000; break;
			case 'LastWeek': durationMilliseconds = 7*24*60*60*1000; break;
			default: console.log('Unknown startTimeString ' + startTimeString); return unknownWeather;
		}
		const startTimeMilliseconds = now - durationMilliseconds;
		const startDate = new Date(startTimeMilliseconds);
		// Set to night if time is <= 6am or >= 6pm AND
		// this is 15Min, 1HOur, or 3Hour
		// Otherwise, stick with daytime icons
		if ( durationMilliseconds <= (3*60*60*1000) && ( startDate.getHours() < 6 || startDate.getHours() > 17 )) {
			timeOfDay = 'night';
		}

		//
		// step 1, was a precip alarm triggered between the startTimeString and now?
		//

		// get that particular trigger
		// if the trigger doesn't exist, this system requires more configuration
		const trigger = triggers ? triggers.filter(t => t.name.includes(alarmName)) : [];
		if ( trigger.length === 0 ) { 
			console.log('Warning: This system does not have a ' + alarmName + ' defined on weather station ' + this.NovaStarPrecipLocId);
			return triggerNotDefined;
		}

		// get alarms resulting from trigger
		const alarms = [];
		if ( precipAlarmsArray != null && precipAlarmsArray.length > 0 ) {
			for ( let i = 0; i < precipAlarmsArray.length; i++ ) {
				const a = precipAlarmsArray[i];
				const t = (new Date ( a.triggerDateTime )).getTime();
				if ( t > startTimeMilliseconds && a?.triggerId == trigger[0].id ) {
					alarms.push(a);
				}
			}
		}
		if ( alarms.length === 0 ) {
			// No measurable precipitation!  Return clear skies.
			return ({
				'icon': timeOfDay + '-clear',
				'summary': 'Clear.'
			});
		}

		// console.log(alarms);
		if ( alarms.length === 0 ) {
			// No measurable precipitation!  Return clear skies.
			return ({
				'icon': timeOfDay + '-clear',
				'summary': 'Clear.'
			});
		}

		//
		// Step 2. Determine lowest temperature at time of alarms
		//
		temperature = this.NovaStarGetLowestTemperature( alarms, temperature );
		if ( temperature == null || temperature > 32 ) {
			// Measurable precipitation but not freezing temperatures!  Return rain.
			return ({
				'icon': timeOfDay + '-rain',
				'summary': 'Rain.  Lowest temperatures of ' + temperature.toFixed(0)
			});
		} else {
			// Measurable precipitation and freezing temperatures!  Return snow.
			return ({
				'icon': timeOfDay + '-snow',
				'summary': 'Snow and temperatures as low as ' + temperature.toFixed(0)
			});
		}

		return unknownWeather;

	}

	// filters all the alarm triggers for those pertaining to PrecipPresent
	// returns an array of json alarm trigger objects
	NovaStarGetAlarmPrecipTriggers(allTriggers) {
		const allTriggersArray = allTriggers?.alarmTriggers;
		const triggers = allTriggersArray ? allTriggersArray.filter(t => t.name.includes('PrecipPresent')) : [];
		// console.log(triggers);
		return triggers;
	}

	// alarms is array of alarm objects - should never be empty because that condition was tested before getting here
	// temperature is unprocessed output of data web services request for temperature time series
	NovaStarGetLowestTemperature( alarms, temperature ) {
		// console.log ( alarms, temperature ) ;
		const ts = temperature?.ts?.data;
		if ( alarms.length == 0 || ts == null || ts.length == 0 ) {
			return null;
		}

		// use last alarm - should be alarms of only one type
		// CAT - really need to process all alarms and find lowest temperature
		const alarm = alarms[alarms.length-1];
		const alarmTime = (new Date (alarm.triggerDateTime)).getTime();

		// get nearest temperature during alarm times
		let done = false;
		let lowestTemperatureValue = ts[0].v;
		let lastIndex = ts.length-1;
		let i = lastIndex;
		// start at the end of the time series array and work backward to find nearest date/time
		while (!done) {
			const tsDataTimeA = (new Date(ts[i].dt)).getTime();
			if ( tsDataTimeA < alarmTime ) {
				done = true;
				// is this between temperature values?  interpolate!  Otherwise, save current value as lowest value
				if ( i == lastIndex ) {
					lowestTemperatureValue = ts[i].v;
				} else {
					const tsValueA = ts[i].v;
					const tsDataTimeB = (new Date(ts[i+1].dt)).getTime();
					const tsValueB = ts[i+1].v;
					// y = yA + (yB-yA)(x-xA)/(xB-xA) where y is temperature and x is date/time
					lowestTemperatureValue = tsValueA + ((tsValueB - tsValueA)*(alarmTime - tsDataTimeA)/(tsDataTimeB - tsDataTimeA));
				}
			} else {
				i--;
				if ( i < 0 ) {
					done = true;
				}
			}
		}

		return lowestTemperatureValue;
	}

	// Given an array of precip trigger objects, filter all the actual alarms
	// on the weather station for those whos id matches a trigger id
	NovaStarGetPrecipAlarmsForWeatherStation (precipTriggersIds, allAlarms) {
		// First, create an array of just the trigger ids
		// console.log(precipTriggersIds, allAlarms );
		const precipTriggersIdsArray = [];
		for ( let i = 0; i < precipTriggersIds.length; i++ ) {
			precipTriggersIdsArray.push ( precipTriggersIds[i].id );
		}
		// console.log(precipTriggersIdsArray);

		const precipAlarmsArray = allAlarms?.alarms;

		const alarms = precipAlarmsArray ? precipAlarmsArray.filter(a => precipTriggersIdsArray.includes( a?.triggerId )) : [];
		// console.log(alarms);
		return alarms;
	}

}
